import { useEffect, useState, useContext } from 'react'
import type { ChatChartCardMarker } from '../../types/chat.js'
import { WorkspaceContext } from '../../context/workspaceContextCore.js'
import { useNls } from '../../nls/nlsProvider.js'
import { getChart } from '../../services/chartService.js'
import { useChart } from '../../hooks/useChart.js'
import type { ChartSpec } from '../../types/chart.js'
import { Chart } from '../Common/Chart/Chart.js'
import { ChartEmbed } from '../Common/Chart/ChartEmbed.js'
import { useIsMemberMobile } from '../../member/useIsMemberMobile.js'
import { getWrapperRole } from '../../utils/wrapperRole.js'
import '../Common/Chart/chart.css'

export interface ChartCardBlockProps {
  card?: ChatChartCardMarker | null
}

const formatSharedAt = (sharedAt: string, locale?: string): string => {
  const date = new Date(sharedAt)
  return Number.isNaN(date.getTime()) ? sharedAt : date.toLocaleString(locale)
}

export const ChartCardBlock = ({ card }: ChartCardBlockProps) => {
  const { t, locale } = useNls()
  const workspaceContext = useContext(WorkspaceContext)
  const activeWorkspaceId = workspaceContext?.activeWorkspaceId
  const workspaces = workspaceContext?.workspaces ?? []
  const activeWorkspace = workspaces.find((w) => w.id === activeWorkspaceId)
  const isMemberMobile = useIsMemberMobile()
  const wrapperRole = getWrapperRole()
  const isPhone = isMemberMobile || wrapperRole === 'member' || wrapperRole === 'helper'

  const [isOnline, setIsOnline] = useState(() => (typeof navigator !== 'undefined' ? navigator.onLine : true))
  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  const [spec, setSpec] = useState<ChartSpec | null>(null)
  const [loadingSpec, setLoadingSpec] = useState(() => Boolean(card?.chartId && activeWorkspaceId && isOnline))
  const [specNotFound, setSpecNotFound] = useState(false)
  const [hasNoAccess, setHasNoAccess] = useState(false)
  const [loadError, setLoadError] = useState(false)

  useEffect(() => {
    if (!card?.chartId || !activeWorkspaceId || !isOnline) {
      return
    }

    let cancelled = false
    getChart(activeWorkspaceId, card.chartId)
      .then((loaded) => {
        if (cancelled) return
        if (!loaded) {
          setSpecNotFound(true)
          setSpec(null)
        } else {
          setSpec(loaded)
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const errObj = err as { code?: string; message?: string }
        if (errObj?.code === 'not-found' || errObj?.message?.includes('not found')) {
          setSpecNotFound(true)
        } else if (errObj?.code === 'permission-denied' || errObj?.message?.includes('permission')) {
          setHasNoAccess(true)
        } else {
          setLoadError(true)
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingSpec(false)
      })

    return () => {
      cancelled = true
    }
  }, [card?.chartId, activeWorkspaceId, isOnline])

  const {
    result,
    loading: queryLoading,
    error: queryError,
    refresh,
  } = useChart({
    workspaceId: activeWorkspaceId ?? '',
    chartId: card?.chartId ?? '',
    spec: spec ?? undefined,
    enabled: Boolean(activeWorkspaceId && card?.chartId && spec && isOnline),
  })

  if (!card) return null

  const queryErrCode = (queryError as { code?: string } | null)?.code
  const isPermissionDenied =
    hasNoAccess ||
    queryErrCode === 'permission-denied' ||
    Boolean(queryError?.message?.includes('permission'))

  const isDeleted = specNotFound
  const isOfflineFallback = !isOnline
  const isNoAccessFallback = isPermissionDenied

  const isLive = Boolean(!isOfflineFallback && !isNoAccessFallback && !isDeleted && spec && result)

  const deepLink =
    activeWorkspace && card.chartId
      ? `/console/workspace/${encodeURIComponent(activeWorkspace.slug)}/analysis?tab=CHARTS&chartId=${encodeURIComponent(card.chartId)}`
      : undefined

  return (
    <section
      className="chatChartCard"
      aria-label={t('NLSChatChartCardLabel', { title: card.title })}
      data-testid="chat-chart-card"
    >
      <strong className="chatChartCard__title">{card.title}</strong>

      {loadingSpec ? (
        <div className="chatChartCard__embed">
          <ChartEmbed status="loading">
            <div className="chatChartCard__loadingPlaceholder" />
          </ChartEmbed>
        </div>
      ) : isLive && spec && result ? (
        <div className="chatChartCard__embed">
          <ChartEmbed
            status={queryLoading && !result ? 'loading' : 'ready'}
            onRefresh={refresh}
            testId="chat-chart-card-embed"
          >
            <Chart spec={spec} data={result} height={200} testId="chat-chart-card-chart" />
          </ChartEmbed>
        </div>
      ) : (
        <div className="chatChartCard__fallback" data-testid="chat-chart-card-fallback">
          {card.snapshotPngUrl ? (
            <img
              className="chatChartCard__snapshot"
              src={card.snapshotPngUrl}
              alt={card.title}
              data-testid="chat-chart-card-snapshot"
            />
          ) : (
            <div
              className="chatChartCard__snapshotPlaceholder"
              aria-label={card.title}
              data-testid="chat-chart-card-placeholder"
            />
          )}
          {isDeleted ? (
            <p className="chatChartCard__deletedNotice" data-testid="chat-chart-card-deleted-text">
              {t('NLSChatChartDeleted')}
            </p>
          ) : hasNoAccess || loadError || !isOnline ? (
            <p className="chatChartCard__fallbackNotice" data-testid="chat-chart-card-fallback-text">
              {t('NLSChatChartShowingSavedPicture')}
            </p>
          ) : null}
        </div>
      )}

      <footer className="chatChartCard__footer">
        <time dateTime={card.sharedAt}>
          {t('NLSChatChartSharedAt', { timestamp: formatSharedAt(card.sharedAt, locale) })}
        </time>
        {!isPhone && deepLink && (
          <a className="chatChartCard__link" href={deepLink}>
            {t('NLSChatChartOpenInCharts')}
          </a>
        )}
      </footer>
    </section>
  )
}
